// AdminWooCommerce Component - WooCommerce API Keys, IPN, and Logs management
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Key, Plus, Globe, FileText, RefreshCw, Trash2, Copy, ShoppingCart, Download, HelpCircle
} from 'lucide-react';
import { useAdmin } from './AdminContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminWooCommerce({ 
  activeTab,
  apiKeys: propApiKeys,
  ipnLogs: propIpnLogs,
  adminKey, 
  onRefreshApiKeys,
  onRefreshIpnLogs,
  onDownloadPlugin,
  onShowGuide
}) {
  // Always call useAdmin hook
  const contextValues = useAdmin() || {};
  
  // Use props if provided, otherwise fallback to context
  const apiKeys = propApiKeys || contextValues.apiKeys || [];
  const ipnLogs = propIpnLogs || contextValues.ipnLogs || [];
  const api = contextValues.api || axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey }
  });
  const fetchApiKeys = onRefreshApiKeys || contextValues.fetchApiKeys || (() => {});
  const fetchIpnLogs = onRefreshIpnLogs || contextValues.fetchIpnLogs || (() => {});
  const formatDate = contextValues.formatDate || ((d) => d ? new Date(d).toLocaleDateString('fr-CA') : 'N/A');
  const language = contextValues.language || 'fr';

  const [loading, setLoading] = useState(false);
  const [newApiKeyDialog, setNewApiKeyDialog] = useState(false);
  const [newShopName, setNewShopName] = useState('');
  const [newShopUrl, setNewShopUrl] = useState('');

  const handleCreateApiKey = async () => {
    if (!newShopName.trim()) {
      toast.error(language === 'fr' ? 'Le nom de la boutique est requis' : 'Shop name is required');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/admin/woocommerce/api-keys', {
        shop_name: newShopName,
        shop_url: newShopUrl
      });
      toast.success(language === 'fr' ? 'Clé API créée!' : 'API Key created!');
      setNewApiKeyDialog(false);
      setNewShopName('');
      setNewShopUrl('');
      fetchApiKeys();
      
      // Show the new key
      if (response.data?.api_key) {
        toast.info(`${language === 'fr' ? 'Nouvelle clé' : 'New key'}: ${response.data.api_key}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la création' : 'Error creating key');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm(language === 'fr' ? 'Révoquer cette clé API?' : 'Revoke this API key?')) return;
    setLoading(true);
    try {
      await api.delete(`/admin/woocommerce/api-keys/${keyId}`);
      toast.success(language === 'fr' ? 'Clé révoquée!' : 'Key revoked!');
      fetchApiKeys();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === 'fr' ? 'Copié!' : 'Copied!');
  };

  // Main WooCommerce overview section
  if (activeTab === 'woocommerce' || activeTab === 'woo-plugin') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                <ShoppingCart className="h-5 w-5 text-white" />
              </div>
              WooCommerce & IPN
            </h2>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Gérez vos clés API et notifications instantanées' : 'Manage your API keys and instant notifications'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchApiKeys}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Actualiser' : 'Refresh'}
            </Button>
            <Button onClick={() => setNewApiKeyDialog(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700">
              <Plus className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Nouvelle Clé API' : 'New API Key'}
            </Button>
          </div>
        </div>

        {/* Plugin Download Card */}
        <Card className="border-2 border-dashed border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <Download className="h-7 w-7 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-lg">
                    {language === 'fr' ? 'Plugin WooCommerce avec IPN' : 'WooCommerce Plugin with IPN'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'Téléchargez la dernière version du plugin (inclut le support IPN)' : 'Download the latest plugin version (includes IPN support)'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {onShowGuide && (
                  <Button variant="outline" onClick={onShowGuide} className="gap-2">
                    <HelpCircle className="h-4 w-4" />
                    {language === 'fr' ? "Guide d'installation" : 'Installation Guide'}
                  </Button>
                )}
                {onDownloadPlugin && (
                  <Button onClick={onDownloadPlugin} className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700" data-testid="download-woocommerce-plugin-btn">
                    <Download className="h-4 w-4" />
                    {language === 'fr' ? 'Télécharger Plugin' : 'Download Plugin'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* API Keys Quick View */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              {language === 'fr' ? 'Clés API' : 'API Keys'} ({apiKeys.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {apiKeys.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Key className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{language === 'fr' ? 'Aucune clé API créée' : 'No API keys created'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {apiKeys.slice(0, 5).map((key) => (
                  <div key={key.api_key_id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium">{key.shop_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{key.api_key ? `${key.api_key.slice(0, 20)}...` : key.api_key_id}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${key.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {key.is_active ? (language === 'fr' ? 'Actif' : 'Active') : (language === 'fr' ? 'Révoqué' : 'Revoked')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New API Key Dialog */}
        <Dialog open={newApiKeyDialog} onOpenChange={setNewApiKeyDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{language === 'fr' ? 'Nouvelle Clé API' : 'New API Key'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{language === 'fr' ? 'Nom de la boutique' : 'Shop Name'}</Label>
                <Input 
                  value={newShopName} 
                  onChange={(e) => setNewShopName(e.target.value)}
                  placeholder={language === 'fr' ? 'Ma Boutique WooCommerce' : 'My WooCommerce Store'}
                />
              </div>
              <div>
                <Label>{language === 'fr' ? 'URL de la boutique (optionnel)' : 'Shop URL (optional)'}</Label>
                <Input 
                  value={newShopUrl} 
                  onChange={(e) => setNewShopUrl(e.target.value)}
                  placeholder="https://maboutique.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewApiKeyDialog(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleCreateApiKey} disabled={loading}>
                {language === 'fr' ? 'Créer' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // API Keys detailed section
  if (activeTab === 'woo-apikeys') {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Key className="h-5 w-5 text-white" />
              </div>
              {language === 'fr' ? 'Clés API WooCommerce' : 'WooCommerce API Keys'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' ? 'Gérez les clés API pour connecter vos boutiques WooCommerce à PayHub' : 'Manage API keys to connect your WooCommerce stores to PayHub'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Button onClick={() => setNewApiKeyDialog(true)} className="bg-gradient-to-r from-purple-500 to-indigo-600">
                <Plus className="h-4 w-4 mr-2" />
                {language === 'fr' ? 'Nouvelle Clé API' : 'New API Key'}
              </Button>
            </div>
            {apiKeys.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Key className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{language === 'fr' ? 'Aucune clé API créée' : 'No API keys created'}</p>
                <p className="text-sm">{language === 'fr' ? 'Créez une clé pour connecter votre boutique WooCommerce' : 'Create a key to connect your WooCommerce store'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((key) => (
                  <div key={key.api_key_id} className="flex items-center justify-between p-4 rounded-xl border border-border">
                    <div>
                      <p className="font-medium">{key.shop_name}</p>
                      <p className="text-sm text-muted-foreground">{key.shop_url || (language === 'fr' ? 'URL non configurée' : 'URL not configured')}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs font-mono text-muted-foreground">{key.api_key ? `${key.api_key.slice(0, 25)}...` : key.api_key_id}</p>
                        {key.api_key && (
                          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => copyToClipboard(key.api_key)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${key.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {key.is_active ? (language === 'fr' ? 'Actif' : 'Active') : (language === 'fr' ? 'Révoqué' : 'Revoked')}
                      </span>
                      {key.is_active && (
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleRevokeApiKey(key.api_key_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New API Key Dialog */}
        <Dialog open={newApiKeyDialog} onOpenChange={setNewApiKeyDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{language === 'fr' ? 'Nouvelle Clé API' : 'New API Key'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{language === 'fr' ? 'Nom de la boutique' : 'Shop Name'}</Label>
                <Input 
                  value={newShopName} 
                  onChange={(e) => setNewShopName(e.target.value)}
                  placeholder={language === 'fr' ? 'Ma Boutique WooCommerce' : 'My WooCommerce Store'}
                />
              </div>
              <div>
                <Label>{language === 'fr' ? 'URL de la boutique (optionnel)' : 'Shop URL (optional)'}</Label>
                <Input 
                  value={newShopUrl} 
                  onChange={(e) => setNewShopUrl(e.target.value)}
                  placeholder="https://maboutique.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewApiKeyDialog(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleCreateApiKey} disabled={loading}>
                {language === 'fr' ? 'Créer' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // IPN Configuration section
  if (activeTab === 'woo-ipn') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
              <Globe className="h-5 w-5 text-white" />
            </div>
            {language === 'fr' ? 'Configuration IPN' : 'IPN Configuration'}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Configurez les notifications instantanées de paiement (IPN) pour vos boutiques' : 'Configure instant payment notifications (IPN) for your stores'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                {language === 'fr' ? "Qu'est-ce que l'IPN ?" : 'What is IPN?'}
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {language === 'fr' 
                  ? "L'IPN (Instant Payment Notification) permet à PayHub de notifier automatiquement votre boutique WooCommerce quand un paiement est effectué. Cela met à jour le statut de la commande instantanément."
                  : 'IPN (Instant Payment Notification) allows PayHub to automatically notify your WooCommerce store when a payment is made. This updates the order status instantly.'}
              </p>
            </div>
            {apiKeys.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{language === 'fr' ? 'Aucune clé API configurée' : 'No API keys configured'}</p>
                <p className="text-sm">{language === 'fr' ? 'Créez d\'abord une clé API dans la section "Clés API"' : 'First create an API key in the "API Keys" section'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div key={key.api_key_id} className="p-4 rounded-xl border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{key.shop_name}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${key.ipn_url ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {key.ipn_url ? (language === 'fr' ? 'IPN Configuré' : 'IPN Configured') : (language === 'fr' ? 'IPN Non configuré' : 'IPN Not configured')}
                      </span>
                    </div>
                    {key.ipn_url && (
                      <p className="text-sm text-muted-foreground font-mono">{key.ipn_url}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // IPN Logs section
  if (activeTab === 'woo-logs') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-500 to-gray-600 flex items-center justify-center shadow-lg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            {language === 'fr' ? 'Logs IPN' : 'IPN Logs'}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Historique des notifications envoyées aux boutiques WooCommerce' : 'History of notifications sent to WooCommerce stores'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ipnLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{language === 'fr' ? 'Aucun log IPN' : 'No IPN logs'}</p>
              <p className="text-sm">{language === 'fr' ? 'Les logs apparaîtront ici après les premiers paiements WooCommerce' : 'Logs will appear here after the first WooCommerce payments'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {ipnLogs.map((log, idx) => (
                <div key={log.ipn_id || idx} className="p-4 rounded-xl border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${log.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {log.success ? (language === 'fr' ? 'Succès' : 'Success') : (language === 'fr' ? 'Échec' : 'Failed')}
                    </span>
                    <p className="text-xs text-muted-foreground">{formatDate(log.created_at)}</p>
                  </div>
                  <p className="text-sm font-mono text-muted-foreground truncate">{log.ipn_url}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
}
